import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { User } from '../../../../shared/models/user';
import { AdminService } from '../../services/admin';
import { MatDialog } from '@angular/material/dialog';
import { UserForm } from '../user-form/user-form';
import { UserFormData } from '../../../../shared/models/user-form-data';
import { Observable } from 'rxjs';
import { PermissionService } from '../../../../core/authorization/permission';

@Component({
	selector: 'app-users',
	standalone: true,
	imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatSortModule, MatPaginatorModule],
	templateUrl: './users.html',
	styleUrl: './users.css'
})
export class Users {
	dataSource       : MatTableDataSource<User> = new MatTableDataSource<User>([]);
	displayedColumns : string[]     = ['uid', 'email', 'displayName', 'disabled', 'creationTime', 'lastSignInTime', 'actions'];
	canCreateUser$ : Observable<boolean>;
	canEditUser$   : Observable<boolean>;
	canDeleteUser$ : Observable<boolean>;

	@ViewChild(MatSort) sort!: MatSort;
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	constructor(private adminService: AdminService, private dialog: MatDialog, private permissionService: PermissionService) {
		this.canCreateUser$ = this.permissionService.hasPermission('users.create');
        this.canEditUser$   = this.permissionService.hasPermission('users.update');
        this.canDeleteUser$ = this.permissionService.hasPermission('users.delete');
	}

	ngOnInit(): void {
		this.loadUsers();
	}

	ngAfterViewInit(): void {
		this.dataSource.sort      = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	async loadUsers(): Promise<void> {
		const result = await this.adminService.getAllUsers();

		if(result) {
			this.dataSource.data = result;
		}
	}

	async onCreateUser(): Promise<void> {
		const dialogRef = this.dialog.open(UserForm, {
			width: '600px',
			disableClose: true,
			data: null
		});

		dialogRef.afterClosed().subscribe(async (result: UserFormData | undefined) => {
			if(result) {
				const success = await this.adminService.createUser({
					email        : result.email,
					password     : result.password,
					displayName  : result.displayName,
					disabled     : result.disabled,
					roleIds      : result.roleIds
				});

				if(success) {
					this.loadUsers();
				}
			}
		});
	}

	async onEditUser(user: User): Promise<void> {
		const formData: UserFormData = {
			uid         : user.uid,
			email       : user.email,
			displayName : user.displayName,
			disabled    : !user.disabled,
			roleIds     : user.roles
		};

		const dialogRef = this.dialog.open(UserForm, {
			width: '600px',
			disableClose: true,
			data: formData
		});

		dialogRef.afterClosed().subscribe(async (result: UserFormData | undefined) => {
			if(result) {
				const userId = result.uid; 

				const updatePayload: Partial<Omit<UserFormData, 'uid'>> = {
					email       : result.email,
					password    : result.password,
					displayName : result.displayName,
					disabled    : !result.disabled,
					roleIds     : result.roleIds
				};

				const success = await this.adminService.updateUser(userId!, updatePayload);

				if(success) {
					this.loadUsers();
				}
			}
		});
	}

	async onDeleteUser(uid: string): Promise<void> {
		if(window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción es irreversible.')) {
			const success = await this.adminService.deleteUser(uid);

			if(success) {
				this.loadUsers();
			}
		}
	}
}
